
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface NotificationPreferences {
  id?: string;
  user_id: string;
  notification_email?: string;
  notification_phone?: string;
  email_new_bookings: boolean;
  email_booking_updates: boolean;
  email_reviews: boolean;
  sms_new_bookings: boolean;
  sms_booking_updates: boolean;
  sms_reviews: boolean;
}

export const useNotificationPreferences = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // First try to get existing preferences
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setPreferences(data);
      } else {
        // Get user profile for defaults
        const { data: profile } = await supabase
          .from('profiles')
          .select('email, phone')
          .eq('id', user.id)
          .single();

        // Create default preferences for customers
        const defaultPreferences: NotificationPreferences = {
          user_id: user.id,
          notification_email: profile?.email || '',
          notification_phone: profile?.phone || '',
          email_new_bookings: true,
          email_booking_updates: true,
          email_reviews: true,
          sms_new_bookings: true,
          sms_booking_updates: true,
          sms_reviews: true,
        };

        setPreferences(defaultPreferences);
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load notification preferences"
      });
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (updatedPreferences: Partial<NotificationPreferences>) => {
    try {
      setSaving(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newPreferences = { ...preferences, ...updatedPreferences };

      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert({
          ...newPreferences,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      setPreferences(data);
      toast({
        title: "Settings Updated",
        description: "Your notification preferences have been saved successfully.",
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save notification preferences"
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  return {
    preferences,
    loading,
    saving,
    savePreferences,
    refetch: fetchPreferences
  };
};
